/**
 * Audio nodes — based on @engine/audio API.
 *
 * AudioEngine methods:
 * - play(clipGuid, options) → AudioSourceInstance
 * - playOneShot(clipGuid, options)
 * - stopVoice(voiceId), stopAll(), stopClip(clipGuid)
 * - fadeOutClip(clipGuid, duration)
 * - loadClip(guid, data), unloadClip(guid), isClipLoaded(guid)
 * - setMasterVolume(volume)
 * - setBusVolume(busId, volumeDb), setBusMute(busId, muted)
 * - transitionToSnapshot(snapshotId, duration)
 * - setListenerState(state), updateListenerFrom2D(...)
 * - setDopplerConfig(factor, speedOfSound)
 *
 * AudioSourceInstance methods:
 * - volume, pitch, loop (setters)
 * - play(), stop(), pause(), resume(buffer), fade(options)
 * - setPosition(x, y, z), updatePositionTracked(x, y, z, dt)
 * - getState() → { playing, paused, currentTime, duration, volume, pitch, loop }
 */
import type { NodeBehaviour } from '@engine/redprint';
import { flowIn, flowOut, input, output } from '@engine/redprint';

// ============================================================================
// PLAYBACK
// ============================================================================

/**
 * Play sound with options
 */
export const PlaySound: NodeBehaviour = {
  id: 'audio.play',
  name: 'Play Sound',
  category: 'Audio',
  color: '#f39c12',
  icon: 'Volume2',
  description: 'Plays a sound and returns a voice handle for control.',
  keywords: ['sfx', 'effect', 'music', 'sound'],
  inputs: [
    flowIn(),
    input('clip', 'Clip GUID', 'string', ''),
    input('volume', 'Volume', 'f32', 1.0),
    input('pitch', 'Pitch', 'f32', 1.0),
    input('loop', 'Loop', 'bool', false),
    input('bus', 'Bus ID', 'string', 'master'),
  ],
  outputs: [flowOut(), output('voice', 'Voice ID', 'i32')],
  compile(ctx) {
    return ctx.emit(
      'bridge:playSound',
      [
        ctx.resolveInput('clip'),
        ctx.resolveInput('volume'),
        ctx.resolveInput('pitch'),
        ctx.resolveInput('loop'),
        ctx.resolveInput('bus'),
      ],
      'i32',
      true,
    );
  },
  execute(ctx) {
    const voice = ctx.bridge.call('bridge:playSound', ctx.getInput<string>('clip'), ctx.getInput<number>('volume'), ctx.getInput<number>('pitch'), ctx.getInput<boolean>('loop'), ctx.getInput<string>('bus')) as number ?? 0;
    ctx.setOutput('voice', voice);
  },
};

/**
 * Play one-shot sound (fire and forget)
 */
export const PlayOneShot: NodeBehaviour = {
  id: 'audio.playOneShot',
  name: 'Play One Shot',
  category: 'Audio',
  color: '#f39c12',
  icon: 'Volume2',
  description: 'Plays a sound effect (fire and forget, no handle returned).',
  keywords: ['sfx', 'effect', 'quick'],
  inputs: [
    flowIn(),
    input('clip', 'Clip GUID', 'string', ''),
    input('volume', 'Volume', 'f32', 1.0),
    input('pitch', 'Pitch', 'f32', 1.0),
    input('bus', 'Bus ID', 'string', 'sfx'),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit(
      'audioEngine.playOneShot',
      [
        ctx.resolveInput('clip'),
        `{ volume: ${ctx.resolveInput('volume')}, pitch: ${ctx.resolveInput('pitch')}, busId: ${ctx.resolveInput('bus')} }`,
      ],
      'void',
      true,
    );
  },
  execute(ctx) {
    ctx.bridge.call('audioEngine.playOneShot', ctx.getInput<string>('clip'), ctx.getInput<number>('volume'), ctx.getInput<number>('pitch'), ctx.getInput<string>('bus'));
  },
};

/**
 * Play spatial sound at position
 */
export const PlaySpatial: NodeBehaviour = {
  id: 'audio.playSpatial',
  name: 'Play Spatial Sound',
  category: 'Audio',
  color: '#f39c12',
  icon: 'Volume2',
  description: 'Plays a 3D positional sound.',
  keywords: ['3d', 'spatial', 'position', 'panner'],
  inputs: [
    flowIn(),
    input('clip', 'Clip GUID', 'string', ''),
    input('x', 'X', 'f32', 0),
    input('y', 'Y', 'f32', 0),
    input('z', 'Z', 'f32', 0),
    input('volume', 'Volume', 'f32', 1.0),
    input('bus', 'Bus ID', 'string', 'sfx'),
  ],
  outputs: [flowOut(), output('voice', 'Voice ID', 'i32')],
  compile(ctx) {
    return ctx.emit(
      'bridge:playSpatial',
      [
        ctx.resolveInput('clip'),
        ctx.resolveInput('x'),
        ctx.resolveInput('y'),
        ctx.resolveInput('z'),
        ctx.resolveInput('volume'),
        ctx.resolveInput('bus'),
      ],
      'i32',
      true,
    );
  },
  execute(ctx) {
    const voice = ctx.bridge.call('bridge:playSpatial', ctx.getInput<string>('clip'), ctx.getInput<number>('x'), ctx.getInput<number>('y'), ctx.getInput<number>('z'), ctx.getInput<number>('volume'), ctx.getInput<string>('bus')) as number ?? 0;
    ctx.setOutput('voice', voice);
  },
};

// ============================================================================
// STOP / CONTROL
// ============================================================================

/**
 * Stop a specific voice
 */
export const StopVoice: NodeBehaviour = {
  id: 'audio.stopVoice',
  name: 'Stop Voice',
  category: 'Audio',
  color: '#f39c12',
  icon: 'VolumeX',
  description: 'Stops a specific playing voice by ID.',
  keywords: ['stop', 'silence', 'mute'],
  inputs: [flowIn(), input('voice', 'Voice ID', 'i32', 0)],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('audioEngine.stopVoice', [ctx.resolveInput('voice')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('audioEngine.stopVoice', ctx.getInput<number>('voice'));
  },
};

/**
 * Stop all voices
 */
export const StopAllSounds: NodeBehaviour = {
  id: 'audio.stopAll',
  name: 'Stop All Sounds',
  category: 'Audio',
  color: '#f39c12',
  icon: 'VolumeX',
  description: 'Stops all currently playing sounds.',
  keywords: ['stop', 'silence', 'mute', 'all'],
  inputs: [flowIn()],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('audioEngine.stopAll', [], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('audioEngine.stopAll');
  },
};

/**
 * Stop all voices playing a specific clip
 */
export const StopClip: NodeBehaviour = {
  id: 'audio.stopClip',
  name: 'Stop Clip',
  category: 'Audio',
  color: '#f39c12',
  icon: 'VolumeX',
  description: 'Stops all voices playing a specific clip.',
  keywords: ['stop', 'clip', 'sound'],
  inputs: [flowIn(), input('clip', 'Clip GUID', 'string', '')],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('audioEngine.stopClip', [ctx.resolveInput('clip')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('audioEngine.stopClip', ctx.getInput<string>('clip'));
  },
};

/**
 * Fade out all voices playing a clip
 */
export const FadeOutClip: NodeBehaviour = {
  id: 'audio.fadeOutClip',
  name: 'Fade Out Clip',
  category: 'Audio',
  color: '#f39c12',
  icon: 'Volume1',
  description: 'Fades out and stops all voices playing a clip.',
  keywords: ['fade', 'stop', 'crossfade'],
  inputs: [flowIn(), input('clip', 'Clip GUID', 'string', ''), input('duration', 'Duration (s)', 'f32', 0.5)],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('audioEngine.fadeOutClip', [ctx.resolveInput('clip'), ctx.resolveInput('duration')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('audioEngine.fadeOutClip', ctx.getInput<string>('clip'), ctx.getInput<number>('duration'));
  },
};

// ============================================================================
// VOLUME / MIXER
// ============================================================================

/**
 * Set master volume
 */
export const SetMasterVolume: NodeBehaviour = {
  id: 'audio.setMasterVolume',
  name: 'Set Master Volume',
  category: 'Audio',
  color: '#f39c12',
  icon: 'Volume2',
  description: 'Sets the master volume (0-1).',
  keywords: ['volume', 'master', 'global'],
  inputs: [flowIn(), input('volume', 'Volume', 'f32', 1.0)],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('audioEngine.setMasterVolume', [ctx.resolveInput('volume')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('audioEngine.setMasterVolume', ctx.getInput<number>('volume'));
  },
};

/**
 * Set bus volume
 */
export const SetBusVolume: NodeBehaviour = {
  id: 'audio.setBusVolume',
  name: 'Set Bus Volume',
  category: 'Audio',
  color: '#f39c12',
  icon: 'Volume2',
  description: 'Sets the volume of an audio bus (in dB).',
  keywords: ['volume', 'bus', 'mixer'],
  inputs: [flowIn(), input('bus', 'Bus ID', 'string', 'master'), input('volume', 'Volume (dB)', 'f32', 0)],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('audioEngine.setBusVolume', [ctx.resolveInput('bus'), ctx.resolveInput('volume')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('audioEngine.setBusVolume', ctx.getInput<string>('bus'), ctx.getInput<number>('volume'));
  },
};

/**
 * Mute/unmute bus
 */
export const SetBusMute: NodeBehaviour = {
  id: 'audio.setBusMute',
  name: 'Set Bus Mute',
  category: 'Audio',
  color: '#f39c12',
  icon: 'VolumeX',
  description: 'Mutes or unmutes an audio bus.',
  keywords: ['mute', 'bus', 'mixer'],
  inputs: [flowIn(), input('bus', 'Bus ID', 'string', 'master'), input('muted', 'Muted', 'bool', true)],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('audioEngine.setBusMute', [ctx.resolveInput('bus'), ctx.resolveInput('muted')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('audioEngine.setBusMute', ctx.getInput<string>('bus'), ctx.getInput<boolean>('muted'));
  },
};

/**
 * Transition to mixer snapshot
 */
export const TransitionToSnapshot: NodeBehaviour = {
  id: 'audio.transitionToSnapshot',
  name: 'Transition To Snapshot',
  category: 'Audio',
  color: '#f39c12',
  icon: 'Sliders',
  description: 'Smoothly transitions to a mixer snapshot.',
  keywords: ['mixer', 'snapshot', 'transition', 'crossfade'],
  inputs: [flowIn(), input('snapshot', 'Snapshot ID', 'string', 'default'), input('duration', 'Duration (s)', 'f32', 1.0)],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('audioEngine.transitionToSnapshot', [ctx.resolveInput('snapshot'), ctx.resolveInput('duration')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('audioEngine.transitionToSnapshot', ctx.getInput<string>('snapshot'), ctx.getInput<number>('duration'));
  },
};

// ============================================================================
// VOICE CONTROL
// ============================================================================

/**
 * Set voice volume
 */
export const SetVoiceVolume: NodeBehaviour = {
  id: 'audio.setVoiceVolume',
  name: 'Set Voice Volume',
  category: 'Audio',
  color: '#f39c12',
  icon: 'Volume2',
  description: 'Sets the volume of a playing voice.',
  keywords: ['volume', 'voice', 'fade'],
  inputs: [flowIn(), input('voice', 'Voice ID', 'i32', 0), input('volume', 'Volume', 'f32', 1.0)],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setVoiceVolume', [ctx.resolveInput('voice'), ctx.resolveInput('volume')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:setVoiceVolume', ctx.getInput<number>('voice'), ctx.getInput<number>('volume'));
  },
};

/**
 * Set voice pitch
 */
export const SetVoicePitch: NodeBehaviour = {
  id: 'audio.setVoicePitch',
  name: 'Set Voice Pitch',
  category: 'Audio',
  color: '#f39c12',
  icon: 'Music',
  description: 'Sets the pitch of a playing voice.',
  keywords: ['pitch', 'voice', 'speed'],
  inputs: [flowIn(), input('voice', 'Voice ID', 'i32', 0), input('pitch', 'Pitch', 'f32', 1.0)],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setVoicePitch', [ctx.resolveInput('voice'), ctx.resolveInput('pitch')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:setVoicePitch', ctx.getInput<number>('voice'), ctx.getInput<number>('pitch'));
  },
};

/**
 * Fade voice
 */
export const FadeVoice: NodeBehaviour = {
  id: 'audio.fadeVoice',
  name: 'Fade Voice',
  category: 'Audio',
  color: '#f39c12',
  icon: 'Volume1',
  description: 'Fades a voice to a target volume.',
  keywords: ['fade', 'voice', 'volume'],
  inputs: [
    flowIn(),
    input('voice', 'Voice ID', 'i32', 0),
    input('target', 'Target Volume', 'f32', 0),
    input('duration', 'Duration (s)', 'f32', 0.5),
    input('stop', 'Stop After Fade', 'bool', false),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit(
      'bridge:fadeVoice',
      [ctx.resolveInput('voice'), ctx.resolveInput('target'), ctx.resolveInput('duration'), ctx.resolveInput('stop')],
      'void',
      true,
    );
  },
  execute(ctx) {
    ctx.bridge.call('bridge:fadeVoice', ctx.getInput<number>('voice'), ctx.getInput<number>('target'), ctx.getInput<number>('duration'), ctx.getInput<boolean>('stop'));
  },
};

/**
 * Pause voice
 */
export const PauseVoice: NodeBehaviour = {
  id: 'audio.pauseVoice',
  name: 'Pause Voice',
  category: 'Audio',
  color: '#f39c12',
  icon: 'Pause',
  description: 'Pauses a playing voice.',
  keywords: ['pause', 'voice'],
  inputs: [flowIn(), input('voice', 'Voice ID', 'i32', 0)],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:pauseVoice', [ctx.resolveInput('voice')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:pauseVoice', ctx.getInput<number>('voice'));
  },
};

/**
 * Resume voice
 */
export const ResumeVoice: NodeBehaviour = {
  id: 'audio.resumeVoice',
  name: 'Resume Voice',
  category: 'Audio',
  color: '#f39c12',
  icon: 'Play',
  description: 'Resumes a paused voice.',
  keywords: ['resume', 'voice', 'unpause'],
  inputs: [flowIn(), input('voice', 'Voice ID', 'i32', 0)],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:resumeVoice', [ctx.resolveInput('voice')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:resumeVoice', ctx.getInput<number>('voice'));
  },
};

/**
 * Set voice position (for spatial audio)
 */
export const SetVoicePosition: NodeBehaviour = {
  id: 'audio.setVoicePosition',
  name: 'Set Voice Position',
  category: 'Audio',
  color: '#f39c12',
  icon: 'MapPin',
  description: 'Sets the 3D position of a spatial voice.',
  keywords: ['position', '3d', 'spatial', 'panner'],
  inputs: [
    flowIn(),
    input('voice', 'Voice ID', 'i32', 0),
    input('x', 'X', 'f32', 0),
    input('y', 'Y', 'f32', 0),
    input('z', 'Z', 'f32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setVoicePosition', [ctx.resolveInput('voice'), ctx.resolveInput('x'), ctx.resolveInput('y'), ctx.resolveInput('z')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:setVoicePosition', ctx.getInput<number>('voice'), ctx.getInput<number>('x'), ctx.getInput<number>('y'), ctx.getInput<number>('z'));
  },
};

// ============================================================================
// QUERIES
// ============================================================================

/**
 * Is voice playing
 */
export const IsVoicePlaying: NodeBehaviour = {
  id: 'audio.isVoicePlaying',
  name: 'Is Voice Playing',
  category: 'Audio',
  isPure: true,
  color: '#f39c12',
  icon: 'Volume2',
  description: 'Returns true if the voice is currently playing.',
  keywords: ['playing', 'voice', 'status'],
  inputs: [input('voice', 'Voice ID', 'i32', 0)],
  outputs: [output('playing', 'Is Playing', 'bool')],
  compile(ctx) {
    return ctx.emit('bridge:isVoicePlaying', [ctx.resolveInput('voice')], 'bool');
  },
  execute(ctx) {
    ctx.setOutput('playing', ctx.bridge.call('bridge:isVoicePlaying', ctx.getInput<number>('voice')) as boolean ?? false);
  },
};

/**
 * Get voice current time
 */
export const GetVoiceTime: NodeBehaviour = {
  id: 'audio.getVoiceTime',
  name: 'Get Voice Time',
  category: 'Audio',
  isPure: true,
  color: '#f39c12',
  icon: 'Clock',
  description: 'Gets the current playback time of a voice.',
  keywords: ['time', 'voice', 'position'],
  inputs: [input('voice', 'Voice ID', 'i32', 0)],
  outputs: [output('time', 'Time (s)', 'f32')],
  compile(ctx) {
    return ctx.emit('bridge:getVoiceTime', [ctx.resolveInput('voice')], 'f32');
  },
  execute(ctx) {
    ctx.setOutput('time', ctx.bridge.call('bridge:getVoiceTime', ctx.getInput<number>('voice')) as number ?? 0);
  },
};

/**
 * Get active voice count
 */
export const GetActiveVoiceCount: NodeBehaviour = {
  id: 'audio.getActiveVoiceCount',
  name: 'Get Active Voice Count',
  category: 'Audio',
  isPure: true,
  color: '#f39c12',
  icon: 'Volume2',
  description: 'Gets the number of currently playing voices.',
  keywords: ['count', 'voices', 'stats'],
  inputs: [],
  outputs: [output('count', 'Count', 'i32')],
  compile(ctx) {
    return ctx.emit('audioEngine.activeVoiceCount', [], 'i32');
  },
  execute(ctx) {
    ctx.setOutput('count', ctx.bridge.call('audioEngine.activeVoiceCount') as number ?? 0);
  },
};

// ============================================================================
// LISTENER
// ============================================================================

/**
 * Update listener position from 2D
 */
export const UpdateListener2D: NodeBehaviour = {
  id: 'audio.updateListener2D',
  name: 'Update Listener 2D',
  category: 'Audio',
  color: '#f39c12',
  icon: 'Headphones',
  description: 'Updates the audio listener position from 2D transform.',
  keywords: ['listener', '2d', 'position', 'spatial'],
  inputs: [
    flowIn(),
    input('x', 'X', 'f32', 0),
    input('y', 'Y', 'f32', 0),
    input('rotation', 'Rotation (rad)', 'f32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit(
      'audioEngine.updateListenerFrom2D',
      [ctx.resolveInput('x'), ctx.resolveInput('y'), ctx.resolveInput('rotation')],
      'void',
      true,
    );
  },
  execute(ctx) {
    ctx.bridge.call('audioEngine.updateListenerFrom2D', ctx.getInput<number>('x'), ctx.getInput<number>('y'), ctx.getInput<number>('rotation'));
  },
};

/**
 * Set doppler configuration
 */
export const SetDopplerConfig: NodeBehaviour = {
  id: 'audio.setDopplerConfig',
  name: 'Set Doppler Config',
  category: 'Audio',
  color: '#f39c12',
  icon: 'Waves',
  description: 'Configures doppler effect parameters.',
  keywords: ['doppler', 'spatial', 'speed'],
  inputs: [
    flowIn(),
    input('factor', 'Doppler Factor', 'f32', 1.0),
    input('speedOfSound', 'Speed of Sound', 'f32', 343),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('audioEngine.setDopplerConfig', [ctx.resolveInput('factor'), ctx.resolveInput('speedOfSound')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('audioEngine.setDopplerConfig', ctx.getInput<number>('factor'), ctx.getInput<number>('speedOfSound'));
  },
};

// ============================================================================
// CLIP MANAGEMENT
// ============================================================================

/**
 * Check if clip is loaded
 */
export const IsClipLoaded: NodeBehaviour = {
  id: 'audio.isClipLoaded',
  name: 'Is Clip Loaded',
  category: 'Audio',
  isPure: true,
  color: '#f39c12',
  icon: 'CheckCircle',
  description: 'Returns true if an audio clip is loaded.',
  keywords: ['loaded', 'clip', 'ready'],
  inputs: [input('clip', 'Clip GUID', 'string', '')],
  outputs: [output('loaded', 'Is Loaded', 'bool')],
  compile(ctx) {
    return ctx.emit('audioEngine.isClipLoaded', [ctx.resolveInput('clip')], 'bool');
  },
  execute(ctx) {
    ctx.setOutput('loaded', ctx.bridge.call('audioEngine.isClipLoaded', ctx.getInput<string>('clip')) as boolean ?? false);
  },
};

/**
 * Unload clip
 */
export const UnloadClip: NodeBehaviour = {
  id: 'audio.unloadClip',
  name: 'Unload Clip',
  category: 'Audio',
  color: '#f39c12',
  icon: 'Trash2',
  description: 'Unloads an audio clip from memory.',
  keywords: ['unload', 'clip', 'memory'],
  inputs: [flowIn(), input('clip', 'Clip GUID', 'string', '')],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('audioEngine.unloadClip', [ctx.resolveInput('clip')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('audioEngine.unloadClip', ctx.getInput<string>('clip'));
  },
};

// ============================================================================
// CONTEXT
// ============================================================================

/**
 * Resume audio context
 */
export const ResumeAudioContext: NodeBehaviour = {
  id: 'audio.resume',
  name: 'Resume Audio',
  category: 'Audio',
  color: '#f39c12',
  icon: 'Play',
  description: 'Resumes the audio context (required after user gesture).',
  keywords: ['resume', 'context', 'unlock'],
  inputs: [flowIn()],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('audioEngine.resume', [], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('audioEngine.resume');
  },
};

/**
 * Suspend audio context
 */
export const SuspendAudioContext: NodeBehaviour = {
  id: 'audio.suspend',
  name: 'Suspend Audio',
  category: 'Audio',
  color: '#f39c12',
  icon: 'Pause',
  description: 'Suspends the audio context (saves CPU).',
  keywords: ['suspend', 'context', 'pause'],
  inputs: [flowIn()],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('audioEngine.suspend', [], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('audioEngine.suspend');
  },
};

// ============================================================================
// EXPORT
// ============================================================================

export const AUDIO_NODES: NodeBehaviour[] = [
  // Playback
  PlaySound,
  PlayOneShot,
  PlaySpatial,
  // Stop
  StopVoice,
  StopAllSounds,
  StopClip,
  FadeOutClip,
  // Volume/Mixer
  SetMasterVolume,
  SetBusVolume,
  SetBusMute,
  TransitionToSnapshot,
  // Voice Control
  SetVoiceVolume,
  SetVoicePitch,
  FadeVoice,
  PauseVoice,
  ResumeVoice,
  SetVoicePosition,
  // Queries
  IsVoicePlaying,
  GetVoiceTime,
  GetActiveVoiceCount,
  // Listener
  UpdateListener2D,
  SetDopplerConfig,
  // Clips
  IsClipLoaded,
  UnloadClip,
  // Context
  ResumeAudioContext,
  SuspendAudioContext,
];
